/**
 * Custom hook for fetching and managing All Farm View data
 * Handles real data fetching with auto-refresh and graceful error handling
 */

var React = require('react');
var { useState, useEffect, useRef } = React;
var { getAllFarmViewData } = require('@managers/allFarmViewManager');

var REFRESH_INTERVAL_MS = 300000; // 5 minutes

/**
 * @typedef {Object} FieldAllFarmData
 * @property {string} fieldId
 * @property {string} fieldName
 * @property {number|null} weeklyPerformance - Weekly irrigation value
 * @property {number|null} weeklyGoal - Weekly irrigation goal
 * @property {boolean} isIrrigating - Whether field is currently irrigating
 * @property {string|null} lastIrrigationStartedAt - ISO timestamp of last irrigation start
 * @property {string|null} lastIrrigationEndedAt - ISO timestamp of last irrigation end
 * @property {'talgil'|'piezometer'|'manual'|'none'|null} lastIrrigationSource - Source of irrigation data
 */

/**
 * @typedef {Object} AllFarmViewDataResponse
 * @property {string} farmingUnitId
 * @property {FieldAllFarmData[]} fields
 * @property {string} units - Unit of measurement ('INCH', 'MM', 'CM')
 * @property {string} dataTimestamp - ISO timestamp of when data was generated
 */

/**
 * @typedef {Object} AllFarmViewState
 * @property {AllFarmViewDataResponse|null} data
 * @property {boolean} isLoading - True during initial load
 * @property {boolean} isRefreshing - True during background refresh
 * @property {Error|null} error - Error from initial load (not refresh)
 * @property {Date|null} lastFetchedAt
 */

/**
 * Hook for fetching All Farm View data with auto-refresh
 * @param {string} farmingUnitId - The farming unit ID to fetch data for
 * @param {string|null} userLengthUnit - User length unit; refetches when changed
 * @returns {AllFarmViewState & { refresh: function, isRealDataEnabled: boolean }}
 */
var useAllFarmViewData = function (farmingUnitId, userLengthUnit) {
  // Simplified: Always enabled if we have ID (feature flag check can be done in parent)
  var isRealDataEnabled = Boolean(farmingUnitId);

  var dataState = useState({
    data: null,
    isLoading: false,
    isRefreshing: false,
    error: null,
    lastFetchedAt: null,
  });
  var currentState = dataState[0];
  var setState = dataState[1];

  // Use ref to track component mount state
  var mountedRef = useRef(true);
  var prevUserLengthUnitRef = useRef(userLengthUnit);
  var prevFarmingUnitIdRef = useRef(farmingUnitId);

  // Track component mount state
  useEffect(function () {
    mountedRef.current = true;
    return function () {
      mountedRef.current = false;
    };
  }, []);

  // Effect for initial fetch and setting up auto-refresh
  useEffect(
    function () {
      // Use a local variable to track if THIS effect instance is still active
      // This is more reliable than a ref when effects re-run
      var isActive = true;
      var lengthUnitChanged = prevUserLengthUnitRef.current !== userLengthUnit;
      var farmingUnitChanged = prevFarmingUnitIdRef.current !== farmingUnitId;
      prevUserLengthUnitRef.current = userLengthUnit;
      prevFarmingUnitIdRef.current = farmingUnitId;
      var shouldClearStaleData = lengthUnitChanged || farmingUnitChanged;

      // Only fetch real data if feature is enabled and ID is present
      if (!isRealDataEnabled || !farmingUnitId) {
        // Only update state if it actually changed (avoid re-render loop)
        setState(function (prev) {
          if (
            prev.data === null &&
            prev.isLoading === false &&
            prev.error === null
          ) {
            return prev; // No change needed
          }
          return {
            data: null,
            isLoading: false,
            isRefreshing: false,
            error: null,
            lastFetchedAt: null,
          };
        });
        // Still return cleanup to ensure any previous interval is cleared
        return function () {
          isActive = false;
        };
      }

      // Define fetch function inside effect - uses farmingUnitId from closure
      var doFetch = function (isRefresh) {
        if (!farmingUnitId || !isActive) {
          return;
        }

        // Don't set loading state for refreshes
        if (!isRefresh) {
          setState(function (prev) {
            return {
              data: shouldClearStaleData ? null : prev.data,
              isLoading: true,
              isRefreshing: false,
              error: null,
              lastFetchedAt: shouldClearStaleData ? null : prev.lastFetchedAt,
            };
          });
        } else {
          setState(function (prev) {
            return {
              data: prev.data,
              isLoading: false,
              isRefreshing: true,
              error: prev.error,
              lastFetchedAt: prev.lastFetchedAt,
            };
          });
        }

        getAllFarmViewData(farmingUnitId)
          .then(function (responseData) {
            if (!isActive) return;

            setState({
              data: responseData,
              isLoading: false,
              isRefreshing: false,
              error: null,
              lastFetchedAt: new Date(),
            });
          })
          .catch(function (err) {
            if (!isActive) return;

            // On refresh error: keep stale data, don't show error
            if (isRefresh) {
              setState(function (prev) {
                return {
                  data: prev.data,
                  isLoading: false,
                  isRefreshing: false,
                  error: null,
                  lastFetchedAt: prev.lastFetchedAt,
                };
              });
            } else {
              setState({
                data: null,
                isLoading: false,
                isRefreshing: false,
                error: err,
                lastFetchedAt: null,
              });
            }
          });
      };

      // Initial fetch
      doFetch(false);

      // Set up auto-refresh interval
      var intervalId = setInterval(function () {
        doFetch(true);
      }, REFRESH_INTERVAL_MS);

      // Cleanup - clear interval and mark inactive
      return function () {
        isActive = false;
        clearInterval(intervalId);
      };
    },
    [farmingUnitId, userLengthUnit],
  );

  /**
   * Manual refresh function
   */
  var refresh = function () {
    if (!isRealDataEnabled || !farmingUnitId) {
      return;
    }

    setState(function (prev) {
      return {
        data: prev.data,
        isLoading: false,
        isRefreshing: true,
        error: prev.error,
        lastFetchedAt: prev.lastFetchedAt,
      };
    });

    getAllFarmViewData(farmingUnitId)
      .then(function (responseData) {
        if (!mountedRef.current) return;

        setState({
          data: responseData,
          isLoading: false,
          isRefreshing: false,
          error: null,
          lastFetchedAt: new Date(),
        });
      })
      .catch(function () {
        if (!mountedRef.current) return;

        setState(function (prev) {
          return {
            data: prev.data,
            isLoading: false,
            isRefreshing: false,
            error: null,
            lastFetchedAt: prev.lastFetchedAt,
          };
        });
      });
  };

  return {
    data: currentState.data,
    isLoading: currentState.isLoading,
    isRefreshing: currentState.isRefreshing,
    error: currentState.error,
    lastFetchedAt: currentState.lastFetchedAt,
    isRealDataEnabled: isRealDataEnabled,
    refresh: refresh,
  };
};

module.exports = useAllFarmViewData;
